import { mergeExpands } from "./mergeExpand.ts";

export interface ODataQuery {
    $skip?: number;
    $top?: number;
    $orderby?: string;
    $select?: string;
    $search?: string;
    $count?: boolean;
    $filter?: string;
    $expand?: string;
}

export interface BuildODataQueryProps {
    select: string[];
    orderby?: { field: string; direction: "asc" | "desc" };
    skip?: number;
    top?: number;
    search?: string;
    count?: boolean;
    filter?: string;
    expand?: string[];
}

export function buildODataQuery({
    select,
    orderby,
    skip,
    top,
    search,
    count = true,
    filter,
    expand
}: BuildODataQueryProps) {
    const query: ODataQuery = {};

    const uniqueSelect = [...new Set(select.filter(Boolean))];

    if (uniqueSelect.length) {
        // We sort the select array, this way even if the columns have been re-ordered, Tanstack query knows it's the same query
        query["$select"] = uniqueSelect.toSorted((a, b) => a.localeCompare(b)).join(",");
    }

    if (skip !== undefined) {
        query["$skip"] = skip;
    }

    if (top !== undefined) {
        query["$top"] = top;
    }

    if (orderby) {
        query["$orderby"] = `${orderby.field} ${orderby.direction}`;
    }

    if (search) {
        query["$search"] = `"${search.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;
    }

    if (count) {
        query["$count"] = true;
    }

    if (filter) {
        query["$filter"] = filter;
    }

    if (expand?.length) {
        const merged = mergeExpands(expand);
        if (merged) {
            query["$expand"] = merged;
        }
    }

    return query;
}

export function stringifyODataQuery(query: ODataQuery): string {
    return Object.entries(query)
        .filter(([, v]) => v !== undefined)
        .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
        .join("&");
}
